// Derives palette-independent UI tokens for the customer booking calendar from the
// venue's booking_site_settings (bss). The booking site has no fixed brand color, so
// every state (selected, today, sold out, hover, focus) is expressed through elevation,
// transparency, weight, and the venue's own accent, never a hardcoded hue. That keeps
// the view readable on any venue palette, dark or light.
//
// Returns a flat object of CSS custom properties to bind on the calendar root via
// :style, e.g. <div :style="theme">. Consume the vars in scoped SCSS with var(--cal-*).

const WHITE = { r: 255, g: 255, b: 255 }
const BLACK = { r: 0, g: 0, b: 0 }

function parseColor(input, fallback) {
  if (!input) return fallback
  const s = String(input).trim()

  let m = s.match(/^#([0-9a-f]{3})$/i)
  if (m) {
    const h = m[1]
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16)
    }
  }

  m = s.match(/^#([0-9a-f]{6})$/i)
  if (m) {
    const h = m[1]
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16)
    }
  }

  m = s.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i)
  if (m) {
    return { r: +m[1], g: +m[2], b: +m[3] }
  }

  return fallback
}

function rgb(c) {
  return 'rgb(' + c.r + ', ' + c.g + ', ' + c.b + ')'
}

function rgba(c, a) {
  return 'rgba(' + c.r + ', ' + c.g + ', ' + c.b + ', ' + a + ')'
}

function relLuminance(c) {
  const ch = [c.r, c.g, c.b].map(v => {
    v = v / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2]
}

function contrastRatio(a, b) {
  const la = relLuminance(a)
  const lb = relLuminance(b)
  const hi = Math.max(la, lb)
  const lo = Math.min(la, lb)
  return (hi + 0.05) / (lo + 0.05)
}

// Theme-neutral default for text-entry field borders. A mid gray at ~0.6 alpha
// reads on both dark and light cards, replacing the old hardcoded dark-blue
// border that was nearly invisible on dark backgrounds. Legacy venues with no
// saved value fall back to this.
export const INPUT_BORDER_DEFAULT = '#7d7d7d99'

// Resolves the border color for text-entry fields: the venue's saved
// input_border_color, or the neutral default when none is set.
export function inputBorderColor(config) {
  config = config || {}
  return config.input_border_color || INPUT_BORDER_DEFAULT
}

// Opaque 6-digit hex form of the input border color, for payment SDKs (Stripe
// appearance, Square card style) and inline gateway styles that can't read CSS
// vars or alpha channels. Any alpha is dropped (flattened to the solid color).
export function inputBorderColorSolid(config) {
  const raw = String(inputBorderColor(config)).trim()
  const m8 = raw.match(/^#([0-9a-f]{8})$/i)
  if (m8) return ('#' + m8[1].slice(0, 6)).toLowerCase()
  const c = parseColor(raw, { r: 125, g: 125, b: 125 })
  return '#' + [c.r, c.g, c.b].map(v => v.toString(16).padStart(2, '0')).join('')
}

// Parses a CSS color into rgb plus alpha (0..1). Accepts #rgb, #rgba, #rrggbb,
// #rrggbbaa and rgb()/rgba(). Returns null for anything else.
function parseColorAlpha(input) {
  if (!input) return null
  const s = String(input).trim()
  let m = s.match(/^#([0-9a-f]{3,4})$/i)
  if (m) {
    const h = m[1]
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
      a: h.length === 4 ? parseInt(h[3] + h[3], 16) / 255 : 1
    }
  }
  m = s.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/i)
  if (m) {
    const h = m[1]
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: m[2] ? parseInt(m[2], 16) / 255 : 1
    }
  }
  m = s.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+%?))?\s*\)$/i)
  if (m) {
    let a = 1
    if (m[4] !== undefined) {
      a = m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4])
      if (isNaN(a)) a = 1
    }
    return { r: +m[1], g: +m[2], b: +m[3], a: Math.min(1, Math.max(0, a)) }
  }
  return null
}

function toHex(c) {
  return '#' + [c.r, c.g, c.b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('')
}

// Opaque 6-digit hex for payment SDKs (Stripe appearance, Square card style),
// which reject alpha. A translucent color is composited over `backdrop` (the
// surface it visually sits on), so the SDK gets the same color the customer
// sees on the page instead of the raw tint. Returns null when `color` is not
// a parseable CSS color; callers decide the fallback.
export function opaqueHex(color, backdrop) {
  const c = parseColorAlpha(color)
  if (!c) return null
  if (c.a >= 1) return toHex(c)
  const under = parseColorAlpha(backdrop) || BLACK
  return toHex({
    r: c.r * c.a + under.r * (1 - c.a),
    g: c.g * c.a + under.g * (1 - c.a),
    b: c.b * c.a + under.b * (1 - c.a)
  })
}

// Template colors for the payment SDKs, each flattened over the surface under
// it. Shared by the booking site checkout and the payment request page.
export function sdkPalette(bss) {
  bss = bss || {}
  const page = opaqueHex(bss.background_color, '#000000') || '#000000'
  const card = opaqueHex(bss.card_color, page) || '#27293d'
  const text = opaqueHex(bss.text_color_card, card) || '#ffffff'
  const accent = opaqueHex(bss.button_color, card) || '#e14eca'
  return { page, card, text, accent }
}

export function buildBookingTheme(bss) {
  bss = bss || {}
  const surface = parseColor(bss.card_color, { r: 30, g: 30, b: 47 })
  const ink = parseColor(bss.text_color_card, WHITE)
  const accent = parseColor(bss.button_color, { r: 225, g: 78, b: 202 })
  const isDark = relLuminance(surface) < 0.5

  // Text that sits on the accent fill (selected day/slot). The venue vetted button_color
  // for actions, so derive a legible foreground by accent luminance.
  const accentInk = relLuminance(accent) < 0.5 ? WHITE : BLACK

  // Low-contrast guardrail: when the venue's ink barely separates from its surface,
  // overlay a neutral scrim so text stays readable without abandoning the palette.
  // Well configured venues never trip this.
  const needsScrim = contrastRatio(ink, surface) < 3.2
  const scrim = needsScrim
    ? (isDark ? 'rgba(18, 18, 24, 0.55)' : 'rgba(255, 255, 255, 0.6)')
    : 'transparent'

  return {
    '--cal-surface': rgb(surface),
    '--cal-ink': rgb(ink),
    '--cal-ink-faint': rgba(ink, 0.55),
    '--cal-ink-ghost': rgba(ink, 0.3),
    '--cal-line': rgba(ink, 0.14),
    '--cal-fill': rgba(ink, isDark ? 0.07 : 0.05),
    '--cal-lift': isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.05)',
    '--cal-accent': rgb(accent),
    '--cal-accent-ink': rgb(accentInk),
    '--cal-accent-soft': rgba(accent, 0.14),
    '--cal-accent-ring': rgba(accent, 0.45),
    '--cal-scrim': scrim,
    '--cal-shadow': isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.15)'
  }
}

export default buildBookingTheme
